import { DAILY_SHOP_POOL, type DailyShopPoolEntry } from "../data/dailyShopData.js";

/** 0~1 난수 소스 — 주입으로 테스트 결정론 확보 (GoldModuleBox와 동일 패턴). */
export type RandomSource = () => number;

/** 슬롯 1칸 — 풀 ID + 이번 리셋 할인율 + 구매(sold-out) 여부. */
export interface DailyShopSlot {
  readonly poolId: string;
  readonly discountPercent: number;
  purchased: boolean;
}

/** 일일상점 영속 상태 — 자정 리셋 시각은 @yjsgames/daily-reset의 dailyResets["daily-shop"]가 SSoT라 여기엔 없다. */
export interface DailyShopState {
  slots: DailyShopSlot[];
  freeRerollUsed: boolean;
  rerollStep: number;
}

/** 리롤 비용 — 무료(1회/일) → 다이아 누진 → 소진(null). */
export type DailyShopRerollCost =
  | { readonly kind: "free" }
  | { readonly kind: "diamond"; readonly amount: number }
  | null;

const SLOT_COUNT = 6;
const DISCOUNT_OPTIONS = [0, 10, 20, 30] as const;
/** 무료 1회 소진 후 다이아 누진 비용. 소진 시 "내일 자정" 비활성 (광고 런타임 없음 → idleblade 광고 리롤 대체). */
const REROLL_DIA_COSTS = [50, 100, 200] as const;

/**
 * 일일상점 진행 — 순수 로직 (Phaser 비의존, 단위 테스트 대상). idleblade RandomShop 골격.
 * 6슬롯 weight 비복원 추첨 + 슬롯별 할인 + sold-out + 무료/다이아 누진 리롤.
 *
 * 자정 리셋 트리거는 @yjsgames/daily-reset의 DailyResetService("daily-shop" 키)가 구동하고,
 * 리셋 발생 시 GameScene이 onReset 콜백에서 applyReset()을 호출한다 (던전 refill()과 동형 — 시각 판정은 패키지, 상태만 여기).
 * 비용 차감·보상 지급(BigNumber/경제)은 GameScene 책임 — 본 컨트롤러는 슬롯/카운터 상태만 관리.
 */
export class DailyShopController {
  private readonly pool: readonly DailyShopPoolEntry[];
  private readonly poolById: Map<string, DailyShopPoolEntry>;
  private readonly rng: RandomSource;
  private slots: DailyShopSlot[];
  private freeRerollUsed: boolean;
  private rerollStep: number;

  constructor(
    rng: RandomSource,
    initial?: Partial<DailyShopState>,
    pool: readonly DailyShopPoolEntry[] = DAILY_SHOP_POOL,
  ) {
    this.rng = rng;
    this.pool = pool;
    this.poolById = new Map(pool.map((e) => [e.id, e]));
    this.freeRerollUsed = initial?.freeRerollUsed ?? false;
    this.rerollStep = Math.max(0, Math.floor(initial?.rerollStep ?? 0));
    // 저장 슬롯 복원 — 알 수 없는 poolId(데이터 변경)는 제거. 빈 상태면 즉시 추첨.
    this.slots = (initial?.slots ?? [])
      .filter((s) => this.poolById.has(s.poolId))
      .map((s) => ({ poolId: s.poolId, discountPercent: s.discountPercent, purchased: s.purchased }));
    if (this.slots.length === 0) this.rollSlots();
  }

  get slotCount(): number {
    return this.slots.length;
  }
  get rerollStepUsed(): number {
    return this.rerollStep;
  }
  get freeRerollAvailable(): boolean {
    return !this.freeRerollUsed;
  }

  /** 슬롯 i의 표시 상태 (poolId/할인/sold-out). 범위 밖이면 undefined. */
  slotAt(i: number): DailyShopSlot | undefined {
    return this.slots[i];
  }
  /** poolId로 풀 엔트리 조회. 알 수 없는 id면 undefined. */
  entryOf(poolId: string): DailyShopPoolEntry | undefined {
    return this.poolById.get(poolId);
  }
  /** 슬롯 i의 풀 엔트리 (보상/비용 계산용). 범위 밖이면 undefined. */
  slotEntry(i: number): DailyShopPoolEntry | undefined {
    const s = this.slots[i];
    return s ? this.poolById.get(s.poolId) : undefined;
  }

  /** 자정 리셋 — @yjsgames/daily-reset onReset 콜백에서 호출 (던전 refill()과 동형). 슬롯 재추첨 + 리롤 카운터/sold-out 리셋. */
  applyReset(): void {
    this.rollSlots();
    this.freeRerollUsed = false;
    this.rerollStep = 0;
  }

  /** 현재 리롤 비용 — 무료 1회 → 다이아 누진 → 소진(null). */
  rerollCost(): DailyShopRerollCost {
    if (!this.freeRerollUsed) return { kind: "free" };
    if (this.rerollStep < REROLL_DIA_COSTS.length) {
      return { kind: "diamond", amount: REROLL_DIA_COSTS[this.rerollStep]! };
    }
    return null;
  }

  /** 리롤 실행 — 호출 측이 비용 지불(다이아 차감) 후 호출. 소진 시 false(무변경). */
  reroll(): boolean {
    const cost = this.rerollCost();
    if (!cost) return false;
    if (cost.kind === "free") this.freeRerollUsed = true;
    else this.rerollStep += 1;
    this.rollSlots();
    return true;
  }

  /** 슬롯 구매 마킹 — 이미 구매했거나 범위 밖이면 false. */
  markPurchased(i: number): boolean {
    const s = this.slots[i];
    if (!s || s.purchased) return false;
    s.purchased = true;
    return true;
  }

  /** weight 기반 비복원 SLOT_COUNT개 추첨 + 슬롯별 할인 랜덤. weight>0 풀만 후보. */
  private rollSlots(): void {
    const available = this.pool.filter((e) => e.weight > 0).slice();
    const picked: DailyShopSlot[] = [];
    const n = Math.min(SLOT_COUNT, available.length);
    for (let k = 0; k < n; k++) {
      const total = available.reduce((sum, e) => sum + Math.max(0, e.weight), 0);
      if (total <= 0) break;
      let roll = this.rng() * total;
      let idx = 0;
      for (; idx < available.length; idx++) {
        roll -= Math.max(0, available[idx]!.weight);
        if (roll < 0) break;
      }
      idx = Math.min(idx, available.length - 1);
      const [entry] = available.splice(idx, 1);
      picked.push({
        poolId: entry!.id,
        discountPercent: DISCOUNT_OPTIONS[Math.floor(this.rng() * DISCOUNT_OPTIONS.length)]!,
        purchased: false,
      });
    }
    this.slots = picked;
  }

  /** 영속 상태 스냅샷 — SaveData 직렬화용 (생성자 initial로 round-trip 복원). 슬롯은 깊은 복사. */
  toState(): DailyShopState {
    return {
      slots: this.slots.map((s) => ({ ...s })),
      freeRerollUsed: this.freeRerollUsed,
      rerollStep: this.rerollStep,
    };
  }
}
